import { Field, Float, ID, ObjectType } from 'type-graphql'
import { AiCreditTransaction } from '@ztechapps/common.contract'
import { AnswerKeyMethodEnum } from '../enums/AnswerKeyMethodEnum'
import { ResultStatusEnum } from '../enums/ResultStatusEnum'
import { ScanBatchStatusEnum } from '../enums/ScanBatchStatusEnum'
import { ScanDetailKindEnum } from '../enums/ScanDetailKindEnum'
import { AnswerKeyItem } from './AnswerKey'
import { ScannedPage, StudentAnswer } from './AssessmentResult'

/**
 * One scanned sheet of a scan, flattened for the admin scan-detail read. Not persisted:
 * assembled per read from an `AssessmentResult` and, on a batch scan, the `ScanBatchItem` that
 * produced it — so an image that never graded still appears, carrying `failed` and the reason in
 * `error` instead of a score, rather than dropping out of the list.
 */
@ObjectType()
export class ScanDetailSheet {
  /**
   * The graded result this sheet was assembled from. Absent for a batch item that never received
   * one — a batch that fails terminally writes every item back flagged failed with no result
   * behind it (`BatchScanQueue.onJobFailed`), and those items are exactly the ones support opens
   * the panel to diagnose, so this cannot be non-null.
   */
  @Field(() => ID, { nullable: true })
  resultId?: string

  @Field(() => String, { nullable: true })
  studentName?: string

  @Field(() => Float)
  totalScore: number

  @Field(() => Float)
  totalItems: number

  @Field(() => Float)
  percentage: number

  /** Absent for an image that produced no graded result. */
  @Field(() => ResultStatusEnum, { nullable: true })
  status?: ResultStatusEnum

  @Field(() => Boolean)
  failed: boolean

  /** Why this image failed to grade. Absent when it graded. */
  @Field(() => String, { nullable: true })
  error?: string

  @Field(() => [ScannedPage])
  pages: ScannedPage[]

  @Field(() => [StudentAnswer])
  answers: StudentAnswer[]
}

/**
 * The work behind one AI usage row, resolved for the admin scan-detail panel: the ledger row
 * itself plus whichever scan its `refId` points at. Not persisted — assembled per read from the
 * transaction and the scan records it references.
 *
 * `kind` says which branch produced this detail. `NoScanLinked` covers all three dead-reference
 * cases at once — a row carrying no reference, a row whose feature key names no scan flow, and a
 * row referencing a record since deleted — and is reported instead of an error so support is
 * never left with a row that cannot be opened. `usage` is populated on every branch, so the panel
 * always has ledger figures to show even when there is no scan behind them.
 */
@ObjectType()
export class ScanDetail {
  @Field(() => ScanDetailKindEnum)
  kind: ScanDetailKindEnum

  /** The ledger row this detail was resolved from. Populated on every branch. */
  @Field(() => AiCreditTransaction)
  usage: AiCreditTransaction

  /** Name of the assessment the scan belongs to. Absent only when no scan is linked. */
  @Field(() => String, { nullable: true })
  assessmentName?: string

  // --- Sheet counts, set on the batch and single-sheet branches only. An answer-key scan grades
  // nothing, so it leaves all three unset rather than reporting zeros, which would read as a scan
  // that graded no sheets. ---

  @Field(() => Float, { nullable: true })
  totalImages?: number

  @Field(() => Float, { nullable: true })
  succeededCount?: number

  @Field(() => Float, { nullable: true })
  failedCount?: number

  @Field(() => ScanBatchStatusEnum, { nullable: true })
  batchStatus?: ScanBatchStatusEnum

  /** Batch-level failure reason — the one that blocks every image, not a single image's. */
  @Field(() => String, { nullable: true })
  batchError?: string

  @Field(() => [ScanDetailSheet])
  sheets: ScanDetailSheet[]

  // --- Answer-key detail, set on the answer-key branch only; the key is embedded on the
  // assessment, which is what the reference points at. ---

  @Field(() => String, { nullable: true })
  answerKeySourceImageUrl?: string

  @Field(() => AnswerKeyMethodEnum, { nullable: true })
  answerKeyCreatedMethod?: AnswerKeyMethodEnum

  @Field(() => [AnswerKeyItem])
  answerKeyItems: AnswerKeyItem[]
}
